/* Full Judge.me widgets stay inert until close to the viewport. The existing
   app supplies its markup, product data, CDN version and module entry point. */
if (!customElements.get('ttv-product-reviews')) {
  customElements.define('ttv-product-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.observer || !this.querySelector('template[data-review-widget]')) return;
      const load = () => {
        this.observer?.disconnect();
        document.querySelectorAll('ttv-product-reviews template[data-review-widget]').forEach(template => {
          const fragment = template.content.cloneNode(true);
          // Restore only Judge.me's own product-data script, never arbitrary script markup.
          fragment.querySelectorAll('script').forEach(old => {
            if (old.classList.contains('jdgm-review-widget-data') && !old.src) {
              const script = document.createElement('script');
              script.textContent = old.textContent;
              document.head.append(script);
              script.remove();
            }
            old.remove();
          });
          const host = template.parentElement;
          host.querySelector('.ttv-review-placeholder')?.remove();
          template.replaceWith(fragment);
        });
        this.loadWidgets().catch(() => {
          // The server-rendered product reviews remain readable if the app CDN fails.
          document.querySelectorAll('ttv-product-reviews .jdgm-ssr-reviews').forEach(e => e.hidden = false);
        });
      };
      this.ratingClick = event => {
        if (!event.target.closest('a[href="#judgeme_product_reviews"]')) return;
        if (!this.querySelector('template[data-review-widget]')) return;
        event.preventDefault();
        load();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };
      document.addEventListener('click', this.ratingClick);
      if (location.hash === '#judgeme_product_reviews') {
        load();
        this.scrollIntoView({ block: 'start' });
        return;
      }
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) load();
        }, { rootMargin: '400px' });
        this.observer.observe(this);
      } else load();
    }
    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('click', this.ratingClick);
    }
    async loadCore() {
      const jdgm = window.jdgm;
      if (typeof jdgm?.$ === 'function' && typeof jdgm.eachRevWidgets === 'function') return;
      if (!jdgm?.loadScript) throw new Error('Review loader unavailable');
      if (!this.constructor.coreReady) {
        this.constructor.coreReady = new Promise((resolve, reject) => {
          const ready = () => {
            clearTimeout(timeout);
            document.removeEventListener('jdgm.finishLoadingCore', ready);
            resolve();
          };
          const timeout = setTimeout(() => {
            document.removeEventListener('jdgm.finishLoadingCore', ready);
            reject(new Error('Review core unavailable'));
          }, 15000);
          document.addEventListener('jdgm.finishLoadingCore', ready, { once: true });
          // Match the app's base -> common ordering. Loading form scripts before
          // common is ready can cause the eachRevWidgets error.
          jdgm.loadScript('widget/base.js', () => {
            jdgm.loadScript('widget/common.js', () => {
              jdgm.triggerVanillaEvent('finishLoadingCore');
              ready();
            });
          });
        });
      }
      await this.constructor.coreReady;
    }
    async loadWidgets() {
      const base = window.jdgm?.CDN_BASE_URL;
      if (!base) return;
      const widgets = [...document.querySelectorAll('ttv-product-reviews .jdgm-review-widget[data-entry-point]')];
      if (!widgets.length) return;
      await this.loadCore();
      const manifest = await fetch(new URL('manifest.json', base)).then(response => {
        if (!response.ok) throw new Error('Review styles unavailable');
        return response.json();
      });
      const visited = new Set();
      const styles = key => {
        if (visited.has(key)) return;
        visited.add(key);
        const entry = manifest[key];
        (entry?.css || []).forEach(file => {
          const url = new URL(file, base).href;
          if ([...document.styleSheets].some(sheet => sheet.href === url)) return;
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = url;
          document.head.append(link);
        });
        (entry?.imports || []).forEach(styles);
      };
      for (const widget of widgets) {
        styles(widget.dataset.entryKey);
        const url = new URL(widget.dataset.entryPoint, base).href;
        if ([...document.scripts].some(script => script.src === url)) continue;
        const script = document.createElement('script');
        script.type = 'module';
        script.src = url;
        document.head.append(script);
      }
    }
  });
}
